"use client";

import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export function ParallaxSection({
  imageSrc,
  imagePos = "center",
  overlay = "rgba(0,0,0,0.45)",
  children,
  minHeight = "100vh",
}: {
  imageSrc: string;
  imagePos?: string;
  overlay?: string;
  children: React.ReactNode;
  minHeight?: string;
}) {
  const sectionRef = useRef<HTMLElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const bg = bgRef.current;
    if (!section || !bg) return;

    const anim = gsap.fromTo(
      bg,
      { yPercent: -15 },
      {
        yPercent: 15,
        ease: "none",
        scrollTrigger: {
          trigger: section,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      },
    );

    return () => {
      anim.scrollTrigger?.kill();
      anim.kill();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      style={{
        position: "relative",
        minHeight,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      <div
        ref={bgRef}
        style={{
          position: "absolute",
          inset: "-20% 0",
          backgroundImage: `url('${imageSrc}')`,
          backgroundSize: "cover",
          backgroundPosition: imagePos,
          backgroundRepeat: "no-repeat",
          willChange: "transform",
        }}
      />
      <div style={{ position: "absolute", inset: 0, background: overlay }} />
      <div
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: "900px",
          padding: "80px 32px",
          textAlign: "center",
        }}
      >
        {children}
      </div>
    </section>
  );
}

export function ContentSection({
  children,
  background = "#080F1A",
  minHeight = "auto",
}: {
  children: React.ReactNode;
  background?: string;
  minHeight?: string;
}) {
  return (
    <section
      style={{
        position: "relative",
        minHeight,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "1100px",
          padding: "80px 32px",
        }}
      >
        {children}
      </div>
    </section>
  );
}
